import { randomUUID } from "node:crypto";
import { s as srcExports } from "../../_virtual/index.js";
import { Analytics } from "@segment/analytics-node";
import { API_TOKENS } from "../../constants/API_TOKENS.js";
import { readPrismicrc } from "../../lib/prismicrc.js";
import { BaseManager } from "../BaseManager.js";
import { HumanSegmentEventType } from "./types.js";
function assertTelemetryInitialized(segmentClient) {
  if (segmentClient === void 0) {
    throw new Error("Telemetry has not been initialized. Run `PrismicManager.telemetry.prototype.initTelemetry()` before re-calling this method.");
  }
}
class TelemetryManager extends BaseManager {
  _segmentClient = void 0;
  _anonymousID = void 0;
  _userID = void 0;
  _context = void 0;
  _experiment = void 0;
  async initTelemetry(args) {
    const isTelemetryEnabled = await this.checkIsTelemetryEnabled();
    this._segmentClient = () => {
      const analytics = new Analytics({
        writeKey: API_TOKENS.SegmentKey,
        // Since it's a local app, we do not benefit from event batching the way a server would normally do, all tracking event will be awaited.
        maxEventsInBatch: 1,
        disable: !isTelemetryEnabled
      });
      analytics.on("error", (error) => {
      });
      return analytics;
    };
    if (isTelemetryEnabled) {
      this.initExperiment();
    }
    this._anonymousID = randomUUID();
    this._context = { app: { name: args.appName, version: args.appVersion } };
  }
  async track(args) {
    const { event, repository, ...properties } = args;
    let repositoryName = repository;
    if (repositoryName === void 0) {
      try {
        repositoryName = await this.project.getRepositoryName();
      } catch {
      }
    }
    const payload = {
      event: HumanSegmentEventType[event],
      properties: {
        nodeVersion: process.versions.node,
        ...properties
      },
      context: { ...this._context }
    };
    payload.anonymousId = this._anonymousID;
    if (this._userID) {
      payload.userId = this._userID;
    }
    if (repositoryName) {
      payload.context ||= {};
      payload.context.groupId ||= {};
      payload.context.groupId.Repository = repositoryName;
    }
    return new Promise((resolve) => {
      assertTelemetryInitialized(this._segmentClient);
      this._segmentClient().track(payload, (maybeError) => {
        resolve();
      });
    });
  }
  async identify(userProfile) {
    const payload = {
      userId: userProfile.shortId,
      anonymousId: this._anonymousID,
      integrations: {
        Intercom: {
          user_hash: userProfile.intercomHash
        }
      },
      context: { ...this._context }
    };
    this._userID = userProfile.shortId;
    return new Promise((resolve) => {
      assertTelemetryInitialized(this._segmentClient);
      this._segmentClient().identify(payload, (maybeError) => {
        resolve();
      });
    });
  }
  async checkIsTelemetryEnabled() {
    let root;
    try {
      root = await this.project.getRoot().catch(() => this.project.suggestRoot());
      return readPrismicrc(root).telemetry !== false;
    } catch {
      return true;
    }
  }
  initExperiment() {
    try {
      this._experiment = srcExports.Experiment.initializeRemote(API_TOKENS.AmplitudeKey);
    } catch (error) {
    }
  }
  async getExperimentVariant(variantKey) {
    if (this._experiment) {
      try {
        const repositoryName = await this.project.getRepositoryName();
        const variants = await this._experiment.fetchV2({
          user_id: this._userID,
          user_properties: {
            Repository: repositoryName
          }
        });
        const variantValue = variants[variantKey].value;
        if (variantValue) {
          await this.track({
            event: "experiment:exposure",
            flag_key: variantKey,
            variant: variantValue
          });
        }
        return variants[variantKey];
      } catch (error) {
      }
    }
    return void 0;
  }
}
export {
  TelemetryManager
};
//# sourceMappingURL=TelemetryManager.js.map
